import { useTranslation } from 'react-i18next';
import { KoobButton, KoobSkeleton } from '@koob/ui';
import { useEffect, useState } from 'react';
import { getHotelsContracts } from '../../../api/node/contracts';
import { getRoomsDetail } from '../../../utils/hotelDistribution';
import ConnectionRequestPageHeader from './Partials/ConnectionRequestPageHeader';
import RequestLoadError from './Partials/RequestLoadError';
import HotelsSelected from './Partials/HotelsSelected';
import HotelsBrowser from './Partials/HotelsBrowser';
import { useApp, useSnack } from '../../../hooks';
import { useAsync } from 'react-async';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  getToConnectionRequestById,
  updateToConnectionRequest,
} from 'api/node/toConnectionRequest';
import KoobLoadingOverlay from '@koob/ui/src/components/KoobLoadingOverlay';
import ToConnectionRequestUpdating from './Partials/ToConnectionRequestUpdating';
import { decodeId } from 'utils';

const ToConnectionRequestDetail = () => {
  const { t } = useTranslation('toConnectionRequest');
  const {
    isAllowedTo,
    canManageToConnectionRequest,
    user,
    currentOrganization,
    selectedDistributionOrganization,
  } = useApp();
  const { notify } = useSnack();
  const { requestId } = useParams();
  const navigate = useNavigate();
  const dmcId =
    selectedDistributionOrganization?.value ?? currentOrganization?.id;
  const {
    data,
    isLoading,
    error: loadError,
    reload,
  } = useAsync({
    promiseFn: getToConnectionRequestById,
    requestId: requestId,
    dmcId: decodeId(dmcId),
  });
  const request = data?.data;

  useEffect(() => {
    if (request) {
      setGlobalMargin(request.margin ?? 0);
      setIsGlobalMarginPercent(request.isMarginPercent ?? false);
    }

    if (request?.requestHotels) {
      let formattedHotels = request?.requestHotels.map((node) => ({
        id: node.hotel?.id,
        displayName: node.hotel?.displayName,
        state: node.hotel?.state,
        country: node.hotel?.city?.country?.alpha2,
        city: node.hotel?.city?.title,
        margin: node.margin,
        isMarginPercent: node.isMarginPercent,
        periods: [],
      }));

      setSelectedHotels(formattedHotels);
    }
  }, [request]);

  const [hotels, setHotels] = useState([]);
  const [selectedHotels, setSelectedHotels] = useState(
    request?.requestHotels || [],
  );
  const [globalMargin, setGlobalMargin] = useState(0);
  const [isGlobalMarginPercent, setIsGlobalMarginPercent] = useState(false);
  const [saving, setSaving] = useState(false);

  const isAllowedToManage = () => {
    return (
      isAllowedTo('canManageToConnectionRequest') ||
      canManageToConnectionRequest(user)
    );
  };

  const changedHotels = selectedHotels?.filter((hotel) => {
    const originalHotel = request?.requestHotels?.find(
      (originalHotel) => originalHotel.hotel.id === hotel.id,
    );
    return (
      !originalHotel ||
      (hotel.margin !== undefined && originalHotel?.margin !== hotel.margin) ||
      (hotel.isMarginPercent !== undefined &&
        originalHotel?.isMarginPercent !== hotel.isMarginPercent) ||
      (hotel.periods !== undefined && hotel.periods.length > 0) ||
      (hotel.rooms !== undefined && hotel.rooms.length > 0) ||
      (hotel.contracts !== undefined && hotel.contracts.length > 0)
    );
  });

  const save = async (state, comment) => {
    setSaving(true);

    try {
      await updateToConnectionRequest({
        toConnectionRequestId: requestId,
        payload: {
          margin: globalMargin ? parseFloat(globalMargin) : 0,
          isMarginPercent: isGlobalMarginPercent,
          state: state ?? request.state,
          explaination: comment?.reason,
          toConnectionRequestHotels: [
            ...changedHotels.map((hotel) => ({
              id: hotel.id,
              margin: hotel.margin ? parseFloat(hotel.margin) : null,
              isMarginPercent: hotel.isMarginPercent,
              periods: hotel.periods,
              rooms: hotel.rooms,
              distribute: true,
            })),
            ...(request.requestHotels
              ?.filter(
                (hotel) => !selectedHotels.some((h) => h.id === hotel.hotel.id),
              )
              .map((requestHotel) => ({
                id: requestHotel.hotel.id,
                distribute: false,
              })) ?? []),
          ],
        },
      });

      notify(t('updated'));
      // The list, not one step back: the type tabs are in the history.
      navigate('/to-connection-request/connections');
    } catch (error) {
      if (error.response?.data?.message) {
        notify(error?.response?.data?.message, { type: 'error' });
      } else {
        notify(t('misc:saveError'), { type: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const setHotelAttribute = (hotelId, attribute, value) => {
    setSelectedHotels((current) =>
      current.map((hotel) => {
        if (hotel.id === hotelId) {
          return {
            ...hotel,
            [attribute]: value,
          };
        }
        return hotel;
      }),
    );
  };

  const isHotelSelected = (hotelId) => {
    return selectedHotels.some((h) => h.id === hotelId);
  };

  const unselectHotel = (hotelId) => {
    setSelectedHotels(selectedHotels.filter((h) => h.id !== hotelId));
  };

  const removeSelectedHotels = (hotelIds) => {
    setSelectedHotels(selectedHotels.filter((h) => !hotelIds.includes(h.id)));
    notify(t('removeSelectedSuccess', { count: hotelIds.length }));
  };

  const convertRoomsToPeriod = (rooms) => {
    const periods = [];

    rooms.forEach((room) => {
      room.periods.forEach((period) => {
        // Nothing entered: the periods inherit the room and season values.
        const newPeriod = {
          id: period.id,
          allotmentCount: null,
          freeSale: null,
          isPeriodAllowedForBooking: true,
          isMarginPercent: null,
          margin: null,
        };

        periods.push(newPeriod);
      });
    });

    return periods;
  };

  const selectHotel = async (hotel) => {
    if (!isHotelSelected(hotel.id)) {
      hotel.margin = globalMargin;
      hotel.isMarginPercent = isGlobalMarginPercent;
      const contracts = await getHotelsContracts({ hotelId: hotel.id });
      const rooms = getRoomsDetail(
        contracts,
        [],
        request?.dmcOrganization,
        request?.toOrganization,
      );

      hotel.periods = convertRoomsToPeriod(rooms);
      setSelectedHotels([...selectedHotels, hotel]);
    } else {
      unselectHotel(hotel.id);
    }
  };

  const selectAllShownHotels = (hotelsList) => {
    const hotelsNotSelected = hotelsList.filter(
      (hotel) => !isHotelSelected(hotel.id),
    );
    hotelsNotSelected.forEach((hotel) => {
      hotel.margin = globalMargin;
      hotel.isMarginPercent = isGlobalMarginPercent;
      hotel.periods = [];
    });
    setSelectedHotels([...selectedHotels, ...hotelsNotSelected]);
  };

  return (
    <>
      <ConnectionRequestPageHeader
        request={request}
        save={save}
        saving={saving}
        isAllowedToManage={isAllowedToManage()}
        loadError={Boolean(loadError)}
      />
      {isLoading ? (
        <KoobSkeleton shape="details" />
      ) : loadError ? (
        <RequestLoadError onRetry={reload} />
      ) : (
        <div className="py-4 px-4">
          {!request?.activeJob ? (
            <>
              <div className="my-5 flex justify-end gap-5">
                {isAllowedToManage() && (
                  <>
                    {saving && (
                      <KoobLoadingOverlay
                        opacity="semi"
                        title={t('toConnectionRequest:saving.title')}
                        description={t(
                          'toConnectionRequest:saving.description',
                        )}
                      />
                    )}
                    <KoobButton type="button" variant="secondary" asChild>
                      <Link to={-1}>{t('misc:cancel')}</Link>
                    </KoobButton>
                    <KoobButton
                      onClick={() => save()}
                      variant="primary"
                      disabled={saving}
                      testId="save-change"
                    >
                      {t('misc:save')}
                    </KoobButton>
                  </>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <HotelsSelected
                  selectedHotels={selectedHotels}
                  setHotelAttribute={setHotelAttribute}
                  onRemoveHotel={unselectHotel}
                  onRemoveSelectedHotels={removeSelectedHotels}
                  dmcOrganization={request?.dmcOrganization}
                  toOrganization={request?.toOrganization}
                  isAllowedToManage={isAllowedToManage()}
                />

                {isAllowedToManage() && (
                  <HotelsBrowser
                    hotels={hotels}
                    setHotels={setHotels}
                    isHotelSelected={isHotelSelected}
                    selectAllShownHotels={selectAllShownHotels}
                    onAddHotel={selectHotel}
                    globalMargin={globalMargin}
                    setGlobalMargin={setGlobalMargin}
                    isGlobalMarginPercent={isGlobalMarginPercent}
                    setIsGlobalMarginPercent={setIsGlobalMarginPercent}
                  />
                )}
              </div>
            </>
          ) : (
            <ToConnectionRequestUpdating />
          )}
        </div>
      )}
    </>
  );
};

export default ToConnectionRequestDetail;
